<template>
  <AnchorWrapper
    id="nutrition-information"
    tag="section"
    :class="sectionClasses ? sectionClasses : 'px-1 c-py-4 c-md:c-py-6 c-lg:c-py-6 bg-primary-200 order-1 c-lg:order-2'"
  >
    <div class="container">
      <div class="row c-justify-center">
        <div :class="isFullWidth ? 'col-12' : 'c-md:col-9 c-lg:col-6'">
          <table
            class="table-base table-striped border-bottom"
            :class="{ 'table-theme-light': themeLight }"
          >
            <thead>
              <tr>
                <td />
                <td class="font-bold">
                  100 {{ product.unit }}
                </td>
                <td
                  v-if="product.servingSize"
                  class="font-bold"
                >
                  {{ product.servingSize }} g / {{ t('portion') }}
                </td>
              </tr>
            </thead>
            <tbody v-if="product.nutrition">
              <tr>
                <td>{{ t('energy_value') }}</td>
                <td class="font-bold whitespace-nowrap">
                  {{ Math.floor(product.nutrition.energy) }} kJ /<br> {{ Math.round(product.nutrition.energy / 4.186) }} kcal
                </td>
                <td
                  v-if="product.servingSize"
                  class="font-bold whitespace-nowrap"
                >
                  {{ Math.floor(onePortionNutrition(product.nutrition.energy)) }} kJ /<br> {{ Math.floor(onePortionNutrition(product.nutrition.energy) / 4.186) }} kcal
                </td>
              </tr>
              <tr>
                <td>{{ t('fats') }}</td>
                <td class="font-bold whitespace-nowrap">
                  {{ product.nutrition.fats }} g
                </td>
                <td
                  v-if="product.servingSize"
                  class="font-bold whitespace-nowrap"
                >
                  {{ onePortionNutrition(product.nutrition.fats) }} g
                </td>
              </tr>
              <tr>
                <td>{{ t('of_which_saturated_fatty_acids') }}</td>
                <td class="font-bold whitespace-nowrap">
                  {{ product.nutrition.saturatedFats }} g
                </td>
                <td
                  v-if="product.servingSize"
                  class="font-bold whitespace-nowrap"
                >
                  {{ onePortionNutrition(product.nutrition.saturatedFats) }} g
                </td>
              </tr>
              <tr>
                <td>{{ t('carbohydrates') }}</td>
                <td class="font-bold whitespace-nowrap">
                  {{ product.nutrition.carbohydrates }} g
                </td>
                <td
                  v-if="product.servingSize"
                  class="font-bold whitespace-nowrap"
                >
                  {{ onePortionNutrition(product.nutrition.carbohydrates) }} g
                </td>
              </tr>
              <tr>
                <td>{{ t('of_which_sugars') }}</td>
                <td class="font-bold whitespace-nowrap">
                  {{ product.nutrition.sugars }} g
                </td>
                <td
                  v-if="product.servingSize"
                  class="font-bold whitespace-nowrap"
                >
                  {{ onePortionNutrition(product.nutrition.sugars) }} g
                </td>
              </tr>
              <tr>
                <td>{{ t('fiber') }}</td>
                <td class="font-bold whitespace-nowrap">
                  {{ product.nutrition.fibre }} g
                </td>
                <td
                  v-if="product.servingSize"
                  class="font-bold whitespace-nowrap"
                >
                  {{ onePortionNutrition(product.nutrition.fibre) }} g
                </td>
              </tr>
              <tr>
                <td>{{ t('proteins') }}</td>
                <td class="font-bold whitespace-nowrap">
                  {{ product.nutrition.proteins }} g
                </td>
                <td
                  v-if="product.servingSize"
                  class="font-bold whitespace-nowrap"
                >
                  {{ onePortionNutrition(product.nutrition.proteins) }} g
                </td>
              </tr>
              <tr>
                <td>{{ t('salt') }}</td>
                <td class="font-bold whitespace-nowrap">
                  {{ product.nutrition.salt }} g
                </td>
                <td
                  v-if="product.servingSize"
                  class="font-bold whitespace-nowrap"
                >
                  {{ onePortionNutrition(product.nutrition.salt) }} g
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </AnchorWrapper>
</template>

<script setup>
import { useI18n } from 'vue-i18n'

import AnchorWrapper from '@/components/general/BaseAnchorWrapper.vue'

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
  isFullWidth: {
    type: Boolean,
    default: false,
  },
  sectionClasses: {
    type: String,
    default: '',
  },
  themeLight: {
    type: Boolean,
    default: false,
  },
})

const { t } = useI18n()

const onePortionNutrition = (input) => {
  if (props.product.servingSize) {
    return ((input / 100) * props.product.servingSize).toFixed(1)
  } return null
};
</script>

<style lang="scss">
.table-theme-light {
  tbody {
    tr {
      &:nth-of-type(even) {
        background-color: $primary-color-100;
      }
    }
  }
}
</style>

<i18n>
  {
    "cs": {
    "carbohydrates": "Sacharidy",
    "energy_value": "Energetická hodnota",
    "fats": "Tuky",
    "fiber": "Vláknina",
    "of_which_saturated_fatty_acids": "z toho nasycené mastné kyseliny",
    "of_which_sugars": "z toho cukry",
    "portion": "porce",
    "proteins": "Bílkoviny",
    "salt": "Sůl"
},
    "sk": {
    "carbohydrates": "Sacharidy",
    "energy_value": "Energia",
    "fats": "Tuky",
    "fiber": "Vláknina",
    "of_which_saturated_fatty_acids": "z toho nasýtené mastné kyseliny",
    "of_which_sugars": "z toho cukry",
    "portion": "porcia",
    "proteins": "Bielkoviny",
    "salt": "Soľ"
},
    "pl": {
    "carbohydrates": "Węglowodany",
    "energy_value": "Wartość energetyczna",
    "fats": "Tłuszcz",
    "fiber": "Błonnik",
    "of_which_saturated_fatty_acids": "w tym kwasy tłuszczowe nasycone",
    "of_which_sugars": "w tym cukry",
    "portion": "porcja",
    "proteins": "Białko",
    "salt": "Sól"
},
    "hu": {
    "carbohydrates": "Szénhidrátok",
    "energy_value": "Energiatartalom",
    "fats": "Zsírok",
    "fiber": "Rost",
    "of_which_saturated_fatty_acids": "ebből telített zsírsavak",
    "of_which_sugars": "ebből cukrok",
    "portion": "adag",
    "proteins": "Fehérje",
    "salt": "Só"
},
    "de": {
    "carbohydrates": "Kohlenhydrate",
    "energy_value": "Energiewert",
    "fats": "Fette",
    "fiber": "Ballaststoffe",
    "of_which_saturated_fatty_acids": "davon gesättigte Fettsäuren",
    "of_which_sugars": "davon Zucker",
    "portion": "Portion",
    "proteins": "Eiweiß",
    "salt": "Salz"
},
    "at": {
    "carbohydrates": "Kohlenhydrate",
    "energy_value": "Energiewert",
    "fats": "Fette",
    "fiber": "Ballaststoffe",
    "of_which_saturated_fatty_acids": "davon gesättigte Fettsäuren",
    "of_which_sugars": "davon Zucker",
    "portion": "Portion",
    "proteins": "Eiweiß",
    "salt": "Salz"
},
    "ro": {
    "carbohydrates": "Carbohidrați",
    "energy_value": "Valoare energetică",
    "fats": "Grăsimi",
    "fiber": "Fibre",
    "of_which_saturated_fatty_acids": "din care acizi grași saturați",
    "of_which_sugars": "din care zaharuri",
    "portion": "porții",
    "proteins": "Proteine",
    "salt": "Sare"
},
    "fr": {
    "carbohydrates": "Glucides",
    "energy_value": "Valeur énergétique",
    "fats": "Matières grasses",
    "fiber": "Fibres alimentaires",
    "of_which_saturated_fatty_acids": "dont acides gras saturés",
    "of_which_sugars": "dont sucres",
    "portion": "portions",
    "proteins": "Protéines",
    "salt": "Sel"
},
    "it": {
    "carbohydrates": "Carboidrati",
    "energy_value": "Energia",
    "fats": "Grassi",
    "fiber": "Fibre",
    "of_which_saturated_fatty_acids": "di cui acidi grassi saturi",
    "of_which_sugars": "di cui zuccheri",
    "portion": "porzione",
    "proteins": "Proteine",
    "salt": "Sale"
}
  }
</i18n>
